import * as React from "react"

/**
 * One glyph per trade, keyed by the `icon` string in data/trades.js.
 *
 * Inline SVG rather than an icon package: the set is a couple of dozen marks,
 * they all want
 * the same stroke weight as the rest of the site, and a dependency that ships
 * a thousand icons to render ten is a download the contractor pays for.
 */

const PATHS = {
  house: <path d="M4 12 14 4l10 8v11a1 1 0 0 1-1 1h-5v-7h-8v7H5a1 1 0 0 1-1-1V12Z" />,
  wrench: (
    <path d="M18.3 5.7a5.5 5.5 0 0 0-7.1 7.1L4 20l4 4 7.2-7.2a5.5 5.5 0 0 0 7.1-7.1l-3.1 3.1-3-3 3.1-3.1Z" />
  ),
  snowflake: (
    <>
      <path d="M14 3v22M4.5 8l19 12M23.5 8l-19 12" />
      <path d="M14 8.5 11 6m3 2.5L17 6M14 19.5 11 22m3-2.5L17 22" />
    </>
  ),
  bolt: <path d="M16 3 6 16h6l-2 9 10-13h-6l2-9Z" />,
  roller: (
    <>
      <rect x="4" y="4" width="16" height="7" rx="1.5" />
      <path d="M20 7.5h3.5v5H14v4" />
      <rect x="11" y="16.5" width="6" height="8" rx="1.5" />
    </>
  ),
  hammer: (
    <>
      <path d="M13 8 8.5 3.5 3 9l4.5 4.5" />
      <path d="M11 10.5 23 22.5 20.5 25 8.5 13" />
      <path d="M13 8l4-4 7 7-4 4" />
    </>
  ),
  tree: (
    <>
      <path d="M14 3 6 14h4l-5 7h18l-5-7h4L14 3Z" />
      <path d="M14 21v5" />
    </>
  ),
  fence: (
    <>
      <path d="M6 4 3 8v17h6V8L6 4ZM19 4l-3 4v17h6V8l-3-4Z" />
      <path d="M3 12h19M3 18h19" />
    </>
  ),
  slab: (
    <>
      <path d="M2 10 14 4l12 6-12 6L2 10Z" />
      <path d="M2 10v7l12 6 12-6v-7" />
    </>
  ),
  planks: (
    <>
      <path d="M3 7h22M3 14h22M3 21h22" />
      <path d="M11 7v7M18 14v7M8 21v3M18 4v3" />
    </>
  ),
  toolbox: (
    <>
      <rect x="3" y="9" width="22" height="14" rx="2" />
      <path d="M10 9V6.5A1.5 1.5 0 0 1 11.5 5h5A1.5 1.5 0 0 1 18 6.5V9" />
      <path d="M3 15h22M11.5 13v4M16.5 13v4" />
    </>
  ),
  broom: (
    <>
      <path d="M17 4 9.5 11.5" />
      <path d="M6 14.5 13.5 22l1.5-6.5 6.5-1.5L14 6.5 12.5 13Z" />
      <path d="M6 14.5 3 25l10.5-3" />
    </>
  ),
  cabinet: (
    <>
      <rect x="4" y="3" width="20" height="22" rx="2" />
      <path d="M14 3v22M4 13h20" />
      <path d="M11.5 8.5v2M16.5 8.5v2M11.5 18v2M16.5 18v2" />
    </>
  ),
  tiles: (
    <>
      <rect x="3" y="3" width="9" height="9" rx="1.5" />
      <rect x="16" y="3" width="9" height="9" rx="1.5" />
      <rect x="3" y="16" width="9" height="9" rx="1.5" />
      <rect x="16" y="16" width="9" height="9" rx="1.5" />
    </>
  ),
  bricks: (
    <>
      <path d="M3 8h22M3 14h22M3 20h22" />
      <path d="M3 4h22v20H3z" />
      <path d="M10 4v4M18 4v4M7 8v6M15 8v6M21 8v6M10 14v6M18 14v6" />
    </>
  ),
  digger: (
    <>
      <path d="M3 22h15" />
      <circle cx="7" cy="22" r="2.6" />
      <circle cx="15" cy="22" r="2.6" />
      <path d="M5 19v-5h9v5" />
      <path d="M11 14 19 5l3 2-5 7" />
      <path d="M17 18h8l-1.5 4h-6.5" />
    </>
  ),
  spark: (
    <>
      <path d="M4 22 14 12" />
      <path d="M11 9 17 3l8 8-6 6Z" />
      <path d="M6 25l-2-3 3-2M20 22h5M21.5 19v6" />
    </>
  ),
  keys: (
    <>
      <circle cx="8" cy="9" r="5" />
      <path d="M11.5 12.5 24 25" />
      <path d="M20 21l3-3M17 18l3-3" />
    </>
  ),
  panel: (
    <>
      <rect x="3" y="4" width="22" height="20" rx="2" />
      <path d="M14 4v20" />
      <path d="M6.5 9.5h4M17.5 9.5h4M6.5 18.5h4M17.5 18.5h4" />
    </>
  ),
  building: (
    <>
      <rect x="4" y="3" width="13" height="22" rx="1.5" />
      <path d="M17 11h7v14h-7" />
      <path d="M8 8h5M8 13h5M8 18h5M20 15h1M20 20h1" />
    </>
  ),
}

const TradeIcon = ({ name, ...props }) => (
  <svg
    viewBox="0 0 28 28"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    {...props}
  >
    {PATHS[name] || PATHS.building}
  </svg>
)

export default TradeIcon
